import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';

const ForInternalToolsPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background antialiased">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            <main className="relative">
                {/* Hero Section */}
                <section className="pt-20 md:pt-32 pb-16 md:pb-24 px-4 relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute top-0 left-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    
                    <div className="container mx-auto max-w-5xl">
                        <div className="text-center space-y-4 md:space-y-6">
                            <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium mb-2 md:mb-4">
                                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                Use Case: Internal Tools
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
                                Build Internal Tools<br className="hidden md:block" />
                                <span className="text-primary">Without Backend Bottlenecks</span>
                            </h1>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Create powerful internal applications that run entirely in the browser. 
                                No servers, no APIs, no infrastructure. Just Python code that works like a document.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-2 sm:px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                            <div className="space-y-6 md:space-y-8">
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">Python-First Development</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Write your entire application in Python, from business logic to UI. Perfect for teams 
                                            who want to build internal tools without learning frontend frameworks.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">Fully Static, Fully Interactive</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Build applications that deploy as static files but maintain full interactivity. 
                                            Perfect for internal tools that need to be shared and used offline.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">No DevOps Overhead</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Run locally or on static hosting—no server management required. 
                                            Deploy and share tools instantly without infrastructure setup.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-4 md:p-6 relative mt-8 md:mt-0">
                                <div className="absolute -top-3 -right-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Live Example
                                </div>
                                <pre className="text-xs md:text-sm text-muted-foreground font-mono overflow-x-auto">
                                    <code>{`# A complete internal tool in one file
import preswald as pw
import pandas as pd

# Load and process data
df = pd.read_csv('sales_data.csv')
summary = df.groupby('region').sum()

# Create interactive UI
app = pw.App("Sales Dashboard")

@app.page("Overview")
def overview():
    return pw.Column([
        pw.Chart(summary, type="bar"),
        pw.Table(df.head()),
        pw.Input("Filter by region", 
                on_change=lambda x: filter_data(x))
    ])

# Export as static file
app.export("sales_dashboard.html")`}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Common Tool Types */}
                <section className="py-16 md:py-24">
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Common Internal Tool Types</h2>
                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Forecast Simulators</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Interactive models for financial forecasting and scenario planning
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Policy Explorers</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Tools to explore and understand complex business policies
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Admin Panels</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Custom interfaces for managing users and content
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">CSV Reviewers</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Interactive tools for data validation and review
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Why Teams Choose Preswald</h2>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Team Efficiency
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Build Without Engineering</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Build without waiting on engineering resources</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Empower analysts and product managers</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Iterate quickly with full control over logic</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Use familiar Python libraries and tools</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Secure Deployment
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Zero Infrastructure</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Runs client-side, offline-capable</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Export as self-contained folder</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">No live database or cloud dependencies</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Share via email, chat, or wiki</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ForInternalToolsPage; 